import { logEvent as logAnalyticsEvent } from '@helpers/Analytics';
import { isDev } from '@helpers/Constants';
import { captureException } from '@helpers/Sentry';

const loggedErrors = new WeakSet<object>();

export function logEvent(name: string, properties?: Record<string, any>) {
  logAnalyticsEvent(name, properties ?? {})
    .then(() => logInfo(`Logged event: ${name}`, { properties }))
    .catch((error) => logError(error));
}

export function logInfo(info: string, ...optionalParams: any[]) {
  maybeInfo(info, ...optionalParams);
}

export function logWarning(warning: string, ...optionalParams: any[]) {
  maybeWarn(warning, ...optionalParams);
}

export function hasLoggedError(error: unknown) {
  return isObject(error) && loggedErrors.has(error);
}

export function markLoggedError(error: unknown) {
  if (isObject(error)) {
    loggedErrors.add(error);
  }
}

export function logError(error: unknown, ...optionalParams: any[]) {
  markLoggedError(error);
  const normalizedError = normalizeError(error);
  markLoggedError(normalizedError);
  maybeError(normalizedError, ...optionalParams);
  try {
    const extra = getErrorExtra(optionalParams);
    if (extra) {
      captureException(normalizedError, { extra });
    } else {
      captureException(normalizedError);
    }
  } catch (e) {
    maybeError(normalizeError(e));
  }
}

function maybeInfo(info: string, ...optionalParams: any[]) {
  if (isDev()) {
    console.log(info, ...optionalParams);
  }
}

function maybeWarn(warning: string, ...optionalParams: any[]) {
  if (isDev()) {
    console.warn(warning, ...optionalParams);
  }
}

function maybeError(error: Error, ...optionalParams: any[]) {
  if (isDev()) {
    console.error(error, ...optionalParams);
  }
}

function getErrorExtra(optionalParams: any[]) {
  if (optionalParams.length === 0) return undefined;
  if (optionalParams.length === 1 && isRecord(optionalParams[0])) return optionalParams[0];

  return { optionalParams };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return isObject(value) && !Array.isArray(value);
}

function isObject(value: unknown): value is object {
  return typeof value === 'object' && value !== null;
}

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  if (typeof error === 'string') return new Error(error);

  return new Error('Unknown error');
}
